import React, { useEffect, useMemo } from "react";

import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import { Stack, Typography } from "@mui/material";
import { BinDeleteIcon, PortfolioAnalysisIcon } from "assets/material-custom-icons";
import { ProgressOverlay, RoleComponentGuard } from "components";
import { AIAnswerText } from "components/Documents/AI-answer-text/AIAnswerText";
import { AIQuestionText } from "components/Documents/AI-question-text/AIQuestionText";
import { EditSet } from "components/Documents/Report-sections-forms/EditSet";
import { Menu } from "components/popups";
import { defaultDocumentReportQueries } from "constants/documents";
import { useActionHook, useModals } from "hooks";
import { usePrompt } from "hooks";
import { useReportsActions, useReportsQueriesSelector } from "store";
import { DocumentReportQueries, DocumentSectionCashflowGraph } from "store/types";

import { RoleName } from "../../../../constants";
import { Chart } from "../../../reports-cashflow/Chart/Chart";
import { useContentContext } from "../../Content/content-context";
import { ReportQueries } from "../ReportQueries";

interface Props {
  section: DocumentSectionCashflowGraph;
}

export const CashflowSection: React.FC<Props> = ({ section }) => {
  const openPrompt = usePrompt();
  const { report_queries: globalQueries, deleteSection, updateSection, cashflowGraphQueue } = useContentContext();
  const { errorModal } = useModals();
  const { getCashflowGraphAction } = useReportsActions();
  const { use_accounting_date } = useReportsQueriesSelector();
  const { settings, queries } = section.report_data;
  const { currency, dateRange, exchangeMethod, filters } = queries || {};

  const { action: getGraph, data: graphData, loading: graphLoading } = useActionHook(getCashflowGraphAction);

  useEffect(() => {
    cashflowGraphQueue.addRequest({
      func: getGraph,
      args: [
        {
          filters,
          currency,
          dateRange,
          exchangeDate: exchangeMethod,
          set_id: settings.set_id || 1,
          use_accounting_date
        }
      ]
    });
  }, [settings.set_id, currency, dateRange, exchangeMethod, filters, use_accounting_date]);

  const { action: updateSectionAction, loading: updateSectionLoading } = useActionHook(updateSection);

  const resultQueries: DocumentReportQueries = useMemo(() => {
    return {
      ...defaultDocumentReportQueries,
      ...(queries || globalQueries)
    };
  }, [queries, globalQueries]);

  const updateReportData = async (data: DocumentSectionCashflowGraph["report_data"]) => {
    try {
      const { report_data } = (await updateSectionAction({
        id: section.id,
        report_data: data
      })) as DocumentSectionCashflowGraph;

      const queries = report_data.queries || resultQueries;

      getGraph({
        filters: queries.filters,
        currency: queries.currency,
        dateRange: queries.dateRange,
        exchangeDate: queries.exchangeMethod,
        set_id: data.settings.set_id,
        use_accounting_date
      });
    } catch (_error) {
      errorModal({ msg: "Failed to update graph section" });
    }
  };

  const updateSet = async () => {
    const updateSet = await openPrompt({
      Component: EditSet,
      props: { data: { set_id: settings.set_id, queries: queries || defaultDocumentReportQueries } }
    });

    if (!updateSet) return;

    updateReportData({
      settings: { set_id: updateSet.set_id || 1 },
      queries: updateSet.queries
    });
  };

  return (
    <Stack gap="10px" width="100%" minHeight="490px">
      {section.parameters?.question && <AIQuestionText question={section.parameters?.question} />}
      {section.parameters?.question && section.text && (
        <Stack flexDirection="row" gap="5px" alignItems="center" justifyContent="flex-start">
          <PortfolioAnalysisIcon sx={{ fill: "#1C1B1F", width: "15px", height: "15px" }} />
          <Typography sx={{ fontWeight: 400, fontSize: "14px", color: "#071F35" }}>Graph</Typography>
        </Stack>
      )}
      {section.text && <AIAnswerText answer={section.text} />}
      <ProgressOverlay
        inProgress={graphLoading || updateSectionLoading}
        containerSX={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          gap: "8px",
          bgcolor: "white",
          borderRadius: "12px",
          boxShadow: "0px 1px 4px 0px #1212171F",
          p: "16px",
          "&:hover": {
            "& .graph-section-menu": {
              opacity: 1
            }
          }
        }}
      >
        <Stack sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontWeight: 500, fontSize: "18px", lineHeight: "24px", color: "#071F35" }}>
            Cashflow
          </Typography>
          <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
            <Menu
              keepTriggerEnabled
              iconButtonProps={{
                sx: { opacity: 0, transition: "opacity .15s linear" },
                size: "sm",
                className: "graph-section-menu"
              }}
              options={[
                {
                  title: "Parameter set",
                  icon: <ViewWeekOutlinedIcon />,
                  onClick: updateSet
                },
                {
                  title: "Delete",
                  icon: <BinDeleteIcon />,
                  onClick: () => deleteSection({ sectionId: section.id })
                }
              ]}
            />
          </RoleComponentGuard>
        </Stack>
        {section.report_data.queries && <ReportQueries {...section.report_data.queries} />}
        <Stack
          sx={{
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "space-between"
          }}
        >
          <Chart data={graphData} chartRef={null} aumData={null} currency={currency} />
        </Stack>
      </ProgressOverlay>
    </Stack>
  );
};
